/**
 * 24h PropertyCard spark for sensor Main.
 * Calls getStatistics as-is; only the returned series is reshaped.
 * npm test src/utest/sidepanel/sensor_priority_spark.test.ts
 */
import type { ChartConfigSeriesData } from 'src/types/Chart';
import type { Device } from 'src/types/Device';
import { getDeviceChartsConfig } from 'src/helpers/statistics/config/device_chart_config';
import { getStatistics } from 'src/helpers/statistics/request/chart_get_statistics_helpers';
import {
	RANGE_OPTIONS,
	rangeDates,
	type ChartControlsDataInterface,
} from 'src/helpers/statistics/ui/chart_control_helpers';

const SPARK_PERIOD = '24_hours';

export const toPropertyCardSpark = (
	series: ChartConfigSeriesData[] | undefined,
): number[] | undefined => {
	if (!series?.length) return;
	const values = series
		.map((point) => point.value)
		.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
	return values.length ? values : undefined;
};

export const fetchSensorPrioritySpark = async (
	device: Device,
	metricId: string,
): Promise<number[] | undefined> => {
	const chartsConfig = getDeviceChartsConfig(device)[0];
	if (!chartsConfig?.url || !metricId) return;
	const chartConfig = chartsConfig.charts?.find((chart) => chart.name === metricId);
	if (!chartConfig) return;
	const rangeOption = RANGE_OPTIONS.find((option) => option.period === SPARK_PERIOD);
	if (!rangeOption) return;

	const chartControlsData: ChartControlsDataInterface = {
		time: rangeDates(new Date(), rangeOption),
		format: 'Y-m-d H:i:s',
		rangeOption,
		currentPeriod: SPARK_PERIOD,
	};

	try {
		const stats = await getStatistics(device, chartControlsData, chartConfig, chartsConfig.url);
		return toPropertyCardSpark(stats?.chartData?.series?.[0]?.data);
	} catch {
		return;
	}
};
